import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { Building2, Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/organizations")({
  component: OrganizationsPage,
});

function OrganizationsPage() {
  const { orgs, refresh, select } = useCurrentOrg();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [list, setList] = useState<any[]>([]);

  useEffect(() => { setList(orgs); }, [orgs]);

  async function create() {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const finalSlug = (slug || name.toLowerCase().replace(/[^a-z0-9]+/g, "-")).slice(0, 60);
    const { data, error } = await supabase
      .from("organizations")
      .insert({ name, slug: finalSlug, created_by: u.user.id })
      .select()
      .single();
    if (error) return toast.error(error.message);
    toast.success("Organization created");
    setOpen(false);
    setName(""); setSlug("");
    await refresh();
    if (data) select(data.id);
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Organizations</h1>
          <p className="text-muted-foreground text-sm mt-1">Each organization has isolated contacts, segments, and campaigns.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New organization</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create organization</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div><Label>Slug (optional)</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-generated" /></div>
              <Button className="w-full" onClick={create} disabled={!name}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {list.map((o) => (
          <Card key={o.id}>
            <CardHeader className="flex flex-row items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>{o.name}</CardTitle>
                <div className="text-xs text-muted-foreground">{o.slug}</div>
              </div>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm" onClick={() => select(o.id)}>Select</Button>
            </CardContent>
          </Card>
        ))}
        {list.length === 0 && (
          <Card className="md:col-span-2"><CardContent className="p-12 text-center text-muted-foreground">No organizations yet. Create one to begin.</CardContent></Card>
        )}
      </div>
    </div>
  );
}
